import React, { useReducer } from 'react';
import SelectableAddresses from '../address/SelectableAddresses';
import { AddressType, SelectOpt } from '../../common/CommonTypes';
import FindAddress, { AddressDetails } from '../../common/address/FindAddress';
import { CommonValidation } from '../../common/commonValidation/commonValidation';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { AddressManagerApi } from '../../../api/AddressManagerApi';
import { CountryApi } from '../../../api/CountryApi';
import { SelectInputHelper } from '../selectInput/SelectInput';
import AddressManagerReducer, {
    AddressSearchState
} from '../../common/address/AddressManagerReducer';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

type AddressManagerProps = {
    addresses: AddressType[];
    addressSelectedIndex: number;
    maxLength?: number;
    editable?: boolean;
    onAddressSelectionChanged: (selectedIndex: number) => void;
};

const AddressManager = React.memo(
    AddressManagerMemo,
    (prevProps, currentProps) => {
        return (
            prevProps.addresses.length === currentProps.addresses.length &&
            prevProps.addressSelectedIndex === currentProps.addressSelectedIndex
        );
    }
);

function AddressManagerMemo({
    addresses,
    addressSelectedIndex,
    maxLength = 2,
    editable,
    onAddressSelectionChanged
}: AddressManagerProps): JSX.Element {
    const { getAccessTokenSilently } = useAuth0();
    const { t } = useTranslation();

    const defaultState: AddressSearchState = {
        showAddressList: false,
        showAddressForm: false,
        addressList: [],
        address: {},
        selectedAddress: '',
        addressErrors: {},
        finding: false,
        searchPostCode: '',
        searchPostCodeError: '',
        modal: false,
        lastSearchedPostCode: '',
        addressEditIndex: -1,
        countries: []
    };

    const [state, dispatch] = useReducer(AddressManagerReducer, defaultState);

    const {
        showAddressList,
        showAddressForm,
        addressList,
        address,
        selectedAddress,
        addressErrors,
        finding,
        searchPostCode,
        searchPostCodeError,
        modal,
        lastSearchedPostCode,
        addressEditIndex,
        countries
    } = state;

    const onChangeDetail = (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => {
        const newAddress = {
            ...address,
            [key]: event.currentTarget.value ?? ''
        };
        dispatch({
            type: 'set-address',
            address: newAddress
        });
    };

    const populateCountries = async () => {
        const successCallback = (result) => {
            const countryOptions = getCountryOptions(result);

            dispatch({
                type: 'set-countries',
                countries: countryOptions
            });
        };

        const failureCallback = () => {
            console.log('Failed to load countries');
        };

        const token = await getAccessTokenSilently();

        new CountryApi().loadCountries(token, successCallback, failureCallback);
    };

    const getCountryOptions = (lookupItems) => {
        const items = [];

        if (lookupItems === undefined || lookupItems === null) return items;

        lookupItems.sort();

        lookupItems.map((item) => {
            const lookupOption = {
                id: item.iso3code,
                text: item.countryName,
                value: item.countryName,
                disabled: false
            };
            items.push(lookupOption);
        });

        return items;
    };

    const onCountryChange = (country: string) => {
        const newAddress = {
            ...address,
            country: country ?? SelectInputHelper.NullValue()
        };
        dispatch({
            type: 'set-address',
            address: newAddress
        });
    };

    const onSelectAddress = async (selectedValue: string) => {
        dispatch({
            type: 'set-selected-address',
            moniker: selectedValue
        });

        if (SelectInputHelper.isNullValue(selectedValue)) {
            //clicing Select... in the address list will clear the address textbox values
            resetAddress();
            setAddressFormVisible();
        } else {
            const token = await getAccessTokenSilently();

            new AddressManagerApi().loadAddress(
                selectedValue,
                token,
                setAddressSuccess,
                setAddressFailed
            );
        }
    };

    const resetAddress = () => {
        dispatch({
            type: 'set-address',
            address: {
                addressLine1: '',
                addressLine2: '',
                townOrCity: '',
                region: '',
                country: '',
                postCode: ''
            }
        });
    };

    const setAddressSuccess = (foundAddress) => {
        if (foundAddress) {
            const country = countries.find(
                (c) => c.text.toLowerCase() === foundAddress.country.toLowerCase()
            );

            dispatch({
                type: 'set-address',
                address: {
                    addressLine1: foundAddress.addressLine1,
                    addressLine2: foundAddress.addressLine2,
                    townOrCity: foundAddress.townOrCity,
                    region: foundAddress.region,
                    country: CommonValidation.isNotEmpty(country.value)
                        ? country.value
                        : SelectInputHelper.NullValue(), //get the user to select the country
                    postCode: foundAddress.postcode
                }
            });
            setAddressFormVisible();
        } else {
            setAddressFailedRoutine();
        }
    };

    const setAddressFailed = (error) => {
        console.log(`Find address error: ${error}`);
        setAddressFailedRoutine();
    };

    const setAddressFailedRoutine = () => {
        resetAddress();
        setAddressFormVisible();
    };

    const setAddressFormVisible = () => {
        dispatch({
            type: 'set-show-address-form',
            show: true
        });
    };

    const onChangeSearchPostCode = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        dispatch({
            type: 'set-search-postcode',
            postcode: event.currentTarget.value
        });
    };

    const validateAddress = (addressToValidate: AddressDetails) => {
        let isValid = true;
        let errors: AddressDetails = {};

        const addressLine1CharLimit = 250;
        const addressLine2CharLimit = 250;
        const postCodeCharLimit = 20;
        const townOrCityCharLimit = 100;

        if (CommonValidation.isEmpty(addressToValidate?.addressLine1)) {
            errors = {
                ...errors,
                addressLine1: t('address_line_1_required_error')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.addressLine1,
                    addressLine1CharLimit
                )
            ) {
                errors = {
                    ...errors,
                    addressLine1: t('too_many_characters_error', {
                        charLimit: addressLine1CharLimit
                    })
                };
                isValid = false;
            }
        }

        if (
            !CommonValidation.isEmpty(addressToValidate?.addressLine2) &&
            !CommonValidation.isValidLength(
                addressToValidate?.addressLine2,
                addressLine2CharLimit
            )
        ) {
            errors = {
                ...errors,
                addressLine2: t('too_many_characters_error', {
                    charLimit: addressLine2CharLimit
                })
            };
            isValid = false;
        }

        if (CommonValidation.isEmpty(addressToValidate?.postCode)) {
            errors = {
                ...errors,
                postCode: t('post_code_is_required_error')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.postCode,
                    postCodeCharLimit
                )
            ) {
                errors = {
                    ...errors,
                    postCode: t('too_many_characters_error', {
                        charLimit: postCodeCharLimit
                    })
                };
                isValid = false;
            }
        }

        if (CommonValidation.isEmpty(addressToValidate?.townOrCity)) {
            errors = {
                ...errors,
                townOrCity: t('town_city_required')
            };
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    addressToValidate?.townOrCity,
                    townOrCityCharLimit
                )
            ) {
                errors = {
                    ...errors,
                    townOrCity: t('too_many_characters_error', {
                        charLimit: townOrCityCharLimit
                    })
                };
                isValid = false;
            }
        }

        if (SelectInputHelper.NullValue() == addressToValidate?.country) {
            errors = {
                ...errors,
                country: t('country_is_required')
            };
            isValid = false;
        }

        dispatch({
            type: 'set-address-errors',
            errors
        });
        return isValid;
    };

    const validatePostcode = () => {
        let isValid = true;
        const postCodeCharLimit = 20;

        dispatch({
            type: 'set-address-errors',
            errors: {}
        });

        if (CommonValidation.isEmpty(searchPostCode)) {
            dispatch({
                type: 'set-search-postcode-error',
                error: t('post_code_required_error')
            });
            isValid = false;
        } else if (searchPostCode.trim().length < 2) {
            dispatch({
                type: 'set-search-postcode-error',
                error: t('post_code_too_short_error')
            });
            isValid = false;
        } else {
            if (
                !CommonValidation.isValidLength(
                    searchPostCode,
                    postCodeCharLimit
                )
            ) {
                isValid = false;
                dispatch({
                    type: 'set-search-postcode-error',
                    error: t('too_many_characters_error', {
                        charLimit: postCodeCharLimit
                    })
                });
            }
        }
        return isValid;
    };

    const truncate = (stringToTruncate, maxLength) => {
        return stringToTruncate.length > maxLength
            ? stringToTruncate.substr(0, maxLength - 1) + '...'
            : stringToTruncate;
    };

    const getLookupItemsAsOptions = (lookupItems, includeSelect) => {
        const items: SelectOpt[] = [];

        const option: SelectOpt = {
            id: '0',
            text: t('select_default'),
            value: SelectInputHelper.NullValue(),
            disabled: false
        };

        if (includeSelect) {
            items.push(option);
        }

        const maxAddressLength = 35;

        lookupItems.map((item, index) => {
            const lookupOption = {
                id: index + 1,
                text: truncate(item.text, maxAddressLength),
                value: item.value,
                disabled: false
            };
            items.push(lookupOption);
        });

        return items;
    };

    const loadAddressesSuccess = (addresses) => {
        const addressOptions = getLookupItemsAsOptions(addresses, true);
        dispatch({
            type: 'load-addresses',
            addresses: addressOptions,
            show: true,
            error: ''
        });
    };

    const loadAddressesFailed = (error) => {
        console.log(`Find address error: ${error}`);
        dispatch({
            type: 'load-addresses',
            addresses: [],
            show: false,
            error: t('address_not_found_error')
        });
        //this function does not reset the address textbox values in case the user has typed them and clicked the find address by mistake
    };

    const onFindClick = async () => {
        const isValid = validatePostcode();

        if (isValid) {
            if (lastSearchedPostCode !== searchPostCode) {
                dispatch({
                    type: 'set-finding',
                    finding: true
                });

                const token = await getAccessTokenSilently();

                new AddressManagerApi().loadAddresses(
                    searchPostCode,
                    token,
                    loadAddressesSuccess,
                    loadAddressesFailed
                );
            }
        }
    };

    const onAddClick = () => {
        if (validateAddress(address)) {
            const repairAddress: AddressType = {
                addressLine1: address.addressLine1 || '',
                addressLine2: address.addressLine2 || '',
                townOrCity: address.townOrCity || '',
                region: address.region || '',
                postcode: address.postCode || '',
                country: address.country || ''
            };
            if (addressEditIndex < 0) {
                addresses.push(repairAddress); // address being changed directly is WRONG!!! needs to be changed
                onAddressSelectionChanged(addresses.length - 1);
            } else {
                addresses[addressEditIndex] = repairAddress;
                onAddressSelectionChanged(addressEditIndex);
            }

            toggleModal();
        }
    };

    const toggleModal = () => {
        if (!countries || countries.length < 1) {
            populateCountries();
        }

        dispatch({
            type: 'set-show-modal',
            show: !modal
        });
    };

    const onAddressEditClick = (index: number) => {
        if (!countries || countries.length < 1) {
            populateCountries();
        }
        dispatch({
            type: 'set-edit-modal',
            editIndex: index,
            address: {
                addressLine1: addresses[index].addressLine1,
                addressLine2: addresses[index].addressLine2,
                townOrCity: addresses[index].townOrCity,
                region: addresses[index].region,
                postCode: addresses[index].postcode,
                country: addresses[index].country
            }
        });
    };

    return (
        <>
            <div className='row repair-request-field-container'>
                <div className='col'>
                    <SelectableAddresses
                        id='addresses'
                        addresses={addresses}
                        selectedIndex={addressSelectedIndex}
                        onSelectionChanged={(selectedIndex: number) => {
                            onAddressSelectionChanged(selectedIndex);
                        }}
                        onEditAddressClick={(selectedIndex: number) => {
                            onAddressEditClick(selectedIndex);
                        }}
                        editable={editable}
                    />
                    {addresses.length < maxLength && (
                        <button
                            id='newAddress'
                            className='btn btn-primary ml-3'
                            onClick={toggleModal}>
                            {addresses.length
                                ? t('alternative_address_button')
                                : t('add_address_button')}
                        </button>
                    )}
                </div>
            </div>
            <Modal isOpen={modal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {addressEditIndex < 0
                        ? t('add_address_title')
                        : t('edit_address_title')}
                </ModalHeader>
                <ModalBody>
                    <FindAddress
                        showAddressForm={showAddressForm}
                        showAddressList={showAddressList}
                        selectedAddress={selectedAddress}
                        addressErrors={addressErrors}
                        addressList={addressList}
                        address={address}
                        searchPostCode={searchPostCode}
                        searchPostCodeError={searchPostCodeError}
                        finding={finding}
                        countries={countries}
                        onSelectAddress={onSelectAddress}
                        onChangeDetail={onChangeDetail}
                        onShowAddressFormClick={setAddressFormVisible}
                        onFindClick={onFindClick}
                        onChangeSearchPostCode={onChangeSearchPostCode}
                        onCountryChange={onCountryChange}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        color='primary'
                        onClick={onAddClick}
                        disabled={!showAddressForm}>
                        {addressEditIndex < 0
                            ? t('button_label.add')
                            : t('button_label.update')}
                    </Button>
                    <Button color='secondary' onClick={toggleModal}>
                        {t('cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default AddressManager;
